import React, { createContext, useContext } from 'react';
import toast, { Toaster, ToastOptions } from 'react-hot-toast';

// Create a context for toast
type ToastFunction = (message: string, options?: ToastOptions) => void;

interface ToastContextType {
  toast: ToastFunction;
  success: ToastFunction;
  error: ToastFunction;
  warning: ToastFunction;
  info: ToastFunction;
}

const ToastContext = createContext<ToastContextType>({
  toast: () => {},
  success: () => {},
  error: () => {},
  warning: () => {},
  info: () => {},
});

// Provider component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Create wrapper functions for each toast type
  const showToast = (message: string, options?: ToastOptions) => {
    toast(message, options);
  };
  
  const showSuccess = (message: string, options?: ToastOptions) => {
    toast.success(message, options);
  };
  
  const showError = (message: string, options?: ToastOptions) => {
    toast.error(message, options);
  };
  
  const showWarning = (message: string, options?: ToastOptions) => {
    toast(message, {
      icon: '⚠️',
      ...options,
    });
  };
  
  const showInfo = (message: string, options?: ToastOptions) => {
    toast(message, {
      icon: 'ℹ️',
      ...options,
    });
  };
  
  return (
    <ToastContext.Provider value={{ 
      toast: showToast,
      success: showSuccess,
      error: showError,
      warning: showWarning,
      info: showInfo,
    }}>
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 5000,
          style: {
            background: '#2D3748',
            color: '#fff',
            border: '1px solid #4A5568',
            maxWidth: '400px',
            width: 'auto',
            padding: '12px 16px',
            marginTop: '10px',
          },
          success: {
            style: {
              background: '#2D3748',
              border: '1px solid #38A169',
            },
            iconTheme: {
              primary: '#38A169',
              secondary: '#fff',
            },
          },
          error: {
            style: {
              background: '#2D3748',
              border: '1px solid #E53E3E',
            },
            iconTheme: {
              primary: '#E53E3E',
              secondary: '#fff',
            },
          },
        }}
        containerStyle={{ zIndex: 100000 }}
      />
      {children}
    </ToastContext.Provider>
  );
};

// Custom hook to use the toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider; 